import { useState } from "react";
import { toast } from "sonner";
import { FileText, Send, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, download, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { INVOICE_STATUS, EMAIL_STATUS, fmtDate, fmtCHF } from "@/lib/format";
import { MapBadge } from "@/components/StatusBadge";
import { Card, Empty, PageHeader, Sel, mapOpts } from "@/components/Field";

export default function InvoicesPage() {
  const { can } = useAuth();
  const [status, setStatus] = useState("");
  const { data, reload } = useFetch(can("invoices", "view") ? `/invoices${status ? `?status=${status}` : ""}` : null);
  const act = async (i, action, msg) => {
    if (action === "cancel" && !window.confirm(`Annullare la fattura ${i.number}? L'operazione non si può annullare.`)) return;
    try {
      await api.post(`/invoices/${i.id}/${action}`);
      toast.success(msg);
    } catch (e) { toast.error(formatError(e)); }
    reload();
  };
  const pdf = (i) => download(`/invoices/${i.id}/pdf`, `Fattura_${i.number}.pdf`).catch((e) => toast.error(formatError(e)));
  if (!can("invoices", "view")) return <Empty testId="invoices-forbidden" text="Non hai il permesso di consultare le fatture." />;

  return (
    <>
      <PageHeader title="Fatture" subtitle="Create durante le uscite di magazzino. Senza IVA, con QR-fattura svizzera." />
      {data && !data.email_configured && (
        <div data-testid="invoices-email-warning" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Il servizio e-mail non è ancora configurato: le fatture vengono create ma non inviate. Puoi scaricarle in PDF e inviarle più tardi con “Invia”.
        </div>
      )}
      <div className="max-w-xs"><Sel testId="invoices-filter-status" value={status} onChange={setStatus} placeholder="Tutti gli stati" options={mapOpts(INVOICE_STATUS)} /></div>
      {data && data.items.length === 0 ? <Empty testId="invoices-empty" text="Nessuna fattura." /> : (
        <Card><ul className="divide-y">
          {data?.items.map((i) => (
            <li key={i.id} data-testid={`invoice-row-${i.id}`} className="px-5 py-4 space-y-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="text-base font-semibold text-slate-900">Fattura {i.number} · {fmtCHF(i.total)}</div>
                  <div className="text-sm text-slate-500">{i.recipient.name} · {i.recipient.email} · {fmtDate(i.date)} · scadenza {fmtDate(i.due_date)}</div>
                  <div className="text-sm text-slate-500">{i.lines.map((l) => `${l.description} ${l.quantity} ${l.unit}`).join(", ")}</div>
                  {i.email_status === "failed" && i.email_error && <div data-testid={`invoice-email-error-${i.id}`} className="text-sm text-red-700">{i.email_error}</div>}
                </div>
                <div className="flex flex-wrap gap-1.5"><MapBadge map={INVOICE_STATUS} value={i.status} /><MapBadge map={EMAIL_STATUS} value={i.email_status} testId={`invoice-email-status-${i.id}`} /></div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button data-testid={`invoice-pdf-${i.id}`} variant="outline" className="h-10" onClick={() => pdf(i)}><FileText className="h-4 w-4 mr-1" /> PDF</Button>
                {i.status !== "cancelled" && can("invoices", "create") && <Button data-testid={`invoice-send-${i.id}`} variant="outline" className="h-10" onClick={() => act(i, "send", "Fattura inviata")}><Send className="h-4 w-4 mr-1" /> {i.email_status === "sent" ? "Invia di nuovo" : "Invia"}</Button>}
                {i.status === "issued" && can("invoices", "approve") && <Button data-testid={`invoice-paid-${i.id}`} className="h-10 bg-green-700 hover:bg-green-800" onClick={() => act(i, "paid", "Segnata come pagata")}><CheckCircle2 className="h-4 w-4 mr-1" /> Pagata</Button>}
                {i.status === "issued" && can("invoices", "approve") && <Button data-testid={`invoice-cancel-${i.id}`} variant="ghost" className="h-10 text-red-700" onClick={() => act(i, "cancel", "Fattura annullata")}><XCircle className="h-4 w-4 mr-1" /> Annulla</Button>}
              </div>
            </li>
          ))}
        </ul></Card>
      )}
    </>
  );
}
